'use client';

import Image from 'next/image';
import Link from 'next/link';
import { ReactNode } from 'react';
import { ButtonShadcn } from '../ui/button-shadcn';

type BannerProps = {
  imageSrc: string;
  imageAlt: string;
  title: ReactNode;
  ctaText: string;
  ctaHref: string;
  children?: ReactNode;
  variant?: 'horizontal' | 'split';
  imagePosition?: 'left' | 'right';
  description?: ReactNode;
};

export const Banner = ({
  imageSrc,
  imageAlt,
  title,
  ctaText,
  ctaHref,
  children,
  variant = 'split',
  imagePosition = 'left',
  description,
}: BannerProps) => {
  if (variant === 'split') {
    const flexDirection = imagePosition === 'right' ? 'lg:flex-row-reverse' : 'lg:flex-row';
    return (
      <div className='bg-zinc-100 rounded-md p-4 '>
        <div className={`flex flex-col ${flexDirection} items-stretch gap-8`}>
          {/* Image Section */}
          <div className='w-full lg:w-1/2 flex-shrink-0'>
            <div className='relative w-full h-[300px] lg:h-[400px] rounded-md overflow-hidden'>
              <Image
                src={imageSrc}
                alt={imageAlt}
                fill
                className='object-cover'
                priority
              />
            </div>
          </div>

          {/* Content Section */}
          <div className='w-full lg:w-1/2 flex flex-col justify-center gap-6 text-center lg:text-left px-4'>
            <h2 className='text-3xl lg:text-4xl font-medium'>
              {title}
            </h2>
            {description && (
              <div className='text-xl font-light'>
                {description}
              </div>
            )}
            <div>
              <ButtonShadcn asChild variant='default' size='lg'>
                <Link href={ctaHref}>{ctaText}</Link>
              </ButtonShadcn>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Horizontal variant
  return (
    <div className='bg-zinc-100 rounded-md p-4 h-full'>
      <div className='flex flex-col lg:flex-row items-center gap-4 h-full'>
        {/* Image Section */}
        <div className='w-full lg:w-1/3 flex-shrink-0'>
          <div className='block sm:hidden lg:block relative w-full aspect-[4/3] rounded-md overflow-hidden'>
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              className='object-cover'
              priority
            />
          </div>
        </div>

        {/* Content Section */}
        <div className='flex-1 flex flex-col items-center lg:items-start text-center lg:text-left'>
          {/* Title and CTA */}
          <div className='flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 mb-8'>
            <h2 className='text-2xl '>
              {title}
            </h2>
            <ButtonShadcn asChild variant='default'>
              <Link href={ctaHref}>{ctaText}</Link>
            </ButtonShadcn>
          </div>

          {/* Features */}
          {children && <div className='w-full flex-1'>{children}</div>}
        </div>
      </div>
    </div>
  );
};
